import React from "react";

import { AsyncSelect, IAsyncSelectProps } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import CompaniesService from "@/services/CompaniesService";

import { renderSwatchOption } from "@/utils/USelect";
import { toWorkspaceOptions } from "@/utils/UWorkspaces";
import { cn } from "@/utils/cn";

import { TCompany } from "@/types";

export interface IClientSelectProps extends Partial<
	IAsyncSelectProps<TCompany>
> {
	/** A single client on file is picked on load - unless the choice is what the select is for (a filter). */
	autoSelectSingle?: boolean;
	/**
	 * Which of the clients that reach the user are offered. Default: the
	 * ones they run (`canOpen`) - a report or an invoice is for those.
	 */
	filter?: (client: TCompany) => boolean;
}

/** The clients a user runs: its page opens for them. Unknown (older rows) counts as theirs. */
export const runsClient = (client: TCompany) => client.canOpen !== false;

export const ClientSelect: React.FC<IClientSelectProps> = ({
	autoSelectSingle = true,
	filter = runsClient,
	...props
}) => {
	// Across workspaces: grouped under each workspace's name once there is
	// more than one, the user's own first.
	const fetchOptions = () =>
		CompaniesService.fetchAll("clients").then((response) => {
			const clients = response.filter(filter);
			if (autoSelectSingle && clients.length === 1) {
				props.onChange?.(clients[0]);
			}

			return toWorkspaceOptions(clients, (client) => ({
				value: client,
				label: client.name,
			}));
		});

	return (
		<AsyncSelect
			loadOptions={fetchOptions}
			isClearable={true}
			getOptionValue={(company) => company.id}
			renderOption={renderSwatchOption<TCompany>(true)}
			{...props}
			label={props.label || t("client.label")}
			placeholder={props.placeholder || t("client.select")}
			className={cn(["w-full", props.className])}
		/>
	);
};
